import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import { openWhatsApp } from "@/lib/whatsapp";
import SeoHead from "@/components/SeoHead";
import { pathFor, ROUTE_KEYS, CAR_BASES } from "@/i18n/routeMap";
import { usePublishSlugMap } from "@/contexts/PageMetaContext";
import { buildLocalBusinessJsonLd, buildBreadcrumb, buildFaqJsonLd, PUBLIC_DOMAIN } from "@/lib/seo";
import { localizeVehicleCategory, dailyRateCopy } from "@/lib/vehicleFacts";

const VIEW_CAR_LABEL = {
  en: "View car", es: "Ver coche", ru: "Смотреть авто", fr: "Voir la voiture",
  de: "Fahrzeug ansehen", it: "Vedi l’auto", nl: "Bekijk de auto", pl: "Zobacz samochód",
};
const RECOMMENDED_TITLE = {
  en: "Recommended cars for this experience",
  es: "Coches recomendados para esta experiencia",
  ru: "Рекомендуемые автомобили для этого опыта",
  fr: "Voitures recommandées pour cette expérience",
  de: "Empfohlene Fahrzeuge für dieses Erlebnis",
  it: "Auto consigliate per questa esperienza",
  nl: "Aanbevolen auto’s voor deze ervaring",
  pl: "Polecane samochody na tę trasę",
};

// Whitelist of tags we allow in generated bodies (HTML from AI SEO Studio).
// Anything else is stripped. This is the SINGLE source of truth for body
// format — the AI produces HTML with these tags only, the editor stores
// HTML, and this renderer displays it safely.
const ALLOWED_TAGS = new Set([
  "h2", "h3", "p", "ul", "ol", "li", "strong", "b", "em", "i", "a", "br"
]);

function sanitizeAndRender(html) {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (node) => {
    // Remove anything that isn't whitelisted.
    Array.from(node.children || []).forEach((child) => {
      const tag = child.tagName?.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        // unwrap: replace element with its text content
        const txt = doc.createTextNode(child.textContent || "");
        child.parentNode.replaceChild(txt, child);
        return;
      }
      // strip dangerous attributes
      Array.from(child.attributes || []).forEach((a) => {
        const n = a.name.toLowerCase();
        if (n === "href" && tag === "a") {
          // allow only http, https or local paths
          const v = a.value.trim();
          if (!/^(https?:\/\/|\/)/i.test(v)) child.removeAttribute(a.name);
        } else if (n === "data-testid") {
          // keep for test hooks
        } else if (n === "class") {
          // keep className styling
        } else {
          child.removeAttribute(a.name);
        }
      });
      walk(child);
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

function renderBody(source) {
  if (!source) return null;
  const looksHtml = /<\s*(h2|h3|p|ul|ol|li|a|strong|em|b|i|br)\b/i.test(source);
  if (looksHtml) {
    const clean = sanitizeAndRender(source);
    return (
      <div
        data-testid="guide-body-html"
        className="prose-slr text-[#F5F1EB]/80 font-light leading-relaxed [&_h2]:font-display [&_h2]:text-[#C9A55A] [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-[#C9A55A] [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:list-inside [&_ol]:space-y-1.5 [&_p]:leading-relaxed [&_p]:mb-3 [&_a]:text-[#C9A55A] [&_a]:underline [&_a]:underline-offset-4 [&_strong]:text-[#F5F1EB]"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }
  // Markdown fallback (legacy content in existing guides).
  const lines = source.split("\n");
  const blocks = [];
  let buf = [];
  const flush = () => {
    if (!buf.length) return;
    const text = buf.join("\n").trim();
    if (text) blocks.push({ type: text.startsWith("- ") ? "ul" : "p", text });
    buf = [];
  };
  for (const ln of lines) {
    if (ln.startsWith("## ")) {
      flush();
      blocks.push({ type: "h2", text: ln.replace(/^##\s+/, "") });
    } else if (ln.trim() === "") {
      flush();
    } else {
      buf.push(ln);
    }
  }
  flush();
  return blocks.map((b, i) => {
    if (b.type === "h2") return <h2 key={i} className="font-display text-[#C9A55A] text-2xl mt-10 mb-3">{b.text}</h2>;
    if (b.type === "ul") {
      return (
        <ul key={i} className="list-disc list-inside text-[#F5F1EB]/80 space-y-1.5 font-light">
          {b.text.split("\n").map((l, j) => <li key={j}>{l.replace(/^-\s+/, "")}</li>)}
        </ul>
      );
    }
    return <p key={i} className="text-[#F5F1EB]/80 font-light leading-relaxed">{b.text}</p>;
  });
}

export default function GuideDetail() {
  const { lang, t } = useLang();
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [settings, setSettings] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setG(null); setNotFound(false);
    (async () => {
      try {
        const [r, s] = await Promise.all([api.get(`/guides/by-slug/${slug}`), api.get("/settings")]);
        setG(r.data); setSettings(s.data || {});
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  usePublishSlugMap(g?.slug || null);

  if (notFound) {
    return (
      <div className="bg-[#0C0C0C] min-h-screen pt-40 text-center text-[#F5F1EB]" data-testid="guide-not-found">
        <h1 className="font-display text-3xl">Guide not found</h1>
        <Link to={pathFor(ROUTE_KEYS.GUIDES, lang)} className="btn-gold mt-8 inline-flex">{t.nav.home || "Back"}</Link>
      </div>
    );
  }
  if (!g) return <div className="bg-[#0C0C0C] min-h-screen pt-40 text-center text-[#8A8A8A]">Loading…</div>;

  const title = pick(g.seoTitle, lang) || pick(g.h1, lang) || pick(g.title, lang);
  const desc = pick(g.metaDescription, lang) || pick(g.intro, lang);
  const h1 = pick(g.h1, lang) || pick(g.title, lang);
  const intro = pick(g.intro, lang);
  const body = pick(g.body, lang);
  const faqs = (g.faqs || []).filter((f) => pick(f.q, lang) && pick(f.a, lang));
  const recommended = Array.isArray(g.recommendedVehicles) ? g.recommendedVehicles : [];
  const heroAlt = lang === "es"
    ? `${title} — alquiler de coches de lujo en Benidorm, Alicante y Costa Blanca`
    : lang === "ru"
      ? `${title} — аренда премиальных автомобилей в Бенидорме, Аликанте и на Коста-Бланке`
      : `${title} — luxury car rental in Benidorm, Alicante and Costa Blanca`;

  const breadcrumb = [
    { name: t.guides?.title || "Guides", path: pathFor(ROUTE_KEYS.GUIDES, lang) },
    { name: title, path: `${pathFor(ROUTE_KEYS.GUIDES, lang)}/${slug}` },
  ];
  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description: desc,
      author: { "@type": "Organization", name: "Spain Luxury Rent Car" },
      publisher: { "@type": "Organization", name: "Spain Luxury Rent Car", url: PUBLIC_DOMAIN },
      datePublished: g.createdAt,
      dateModified: g.updatedAt,
    },
    buildBreadcrumb(breadcrumb),
    buildFaqJsonLd(faqs, lang),
  ];

  return (
    <div className="bg-[#0C0C0C] min-h-screen" data-testid={`guide-detail-${slug}`}>
      <SeoHead title={title} description={desc} image={g.coverImage} lang={lang}
        pageKey={ROUTE_KEYS.GUIDE_DETAIL} slug={slug} slugMap={g.slug}
        noindex={g.noindex || !g.active || g.seoEnabled === false}
        readyLocales={Object.entries(g.localeStatus || {}).filter(([, status]) => status === "READY").map(([locale]) => locale)}
        jsonLd={jsonLd} />

      <article className="pt-28 md:pt-32 pb-24 px-6 md:px-10 max-w-3xl mx-auto">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-[#8A8A8A] flex items-center gap-2 mb-4">
          <Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="hover:text-[#C9A55A]">{t.nav.home || "Home"}</Link>
          <ChevronRight size={12} />
          <Link to={pathFor(ROUTE_KEYS.GUIDES, lang)} className="hover:text-[#C9A55A]">{t.guides?.title || "Guides"}</Link>
          <ChevronRight size={12} />
          <span className="text-[#C9A55A] truncate">{h1}</span>
        </nav>

        <h1 className="font-display text-[#F5F1EB] text-3xl md:text-5xl leading-tight">{h1}</h1>
        {g.coverImage && (
          <div className="mt-8 overflow-hidden border border-[#2A2A2A]" data-testid="guide-hero-image">
            <img
              src={g.coverImage}
              alt={heroAlt}
              loading="lazy"
              className="w-full h-[260px] md:h-[420px] object-cover"
            />
          </div>
        )}
        {intro && <p className="text-[#F5F1EB]/85 font-light mt-6 text-lg leading-relaxed">{intro}</p>}

        <div className="mt-10 space-y-4">{renderBody(body)}</div>

        {recommended.length >= 2 && (
          <section className="mt-16" data-testid="guide-recommended-cars">
            <div className="text-overline text-[#C9A55A] mb-4">{RECOMMENDED_TITLE[lang] || RECOMMENDED_TITLE.en}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {recommended.slice(0, 3).map((v) => {
                const href = `${CAR_BASES[lang]}/${v.slug}`;
                return (
                  <Link
                    key={v.id || v.slug}
                    to={href}
                    className="group border border-[#2A2A2A] hover:border-[#C9A55A]/60 transition-colors block"
                    data-testid={`guide-rec-car-${v.slug}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
                      {v.image ? (
                        <img
                          src={v.image}
                          alt={`${v.name} — ${RECOMMENDED_TITLE[lang]}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : null}
                    </div>
                    <div className="p-3">
                      <div className="font-display text-[#F5F1EB] text-base leading-tight truncate">{v.name}</div>
                {v.categoryKey && (
                        <div className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em] mt-1">{pick(v.categoryLabel, lang) || localizeVehicleCategory(v.categoryKey, lang)}</div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        {v.priceDaily ? (
                          <div className="text-[#C9A55A] text-sm">
                      {dailyRateCopy(lang).from} €{v.priceDaily} {dailyRateCopy(lang).unit}
                          </div>
                        ) : <span />}
                        <span className="text-[11px] text-[#C9A55A] uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:text-[#F5F1EB]">
                          {VIEW_CAR_LABEL[lang] || VIEW_CAR_LABEL.en} <ArrowRight size={12}/>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-12 border border-[#2A2A2A] p-6 flex flex-col gap-3 items-center text-center">
          <button onClick={() => openWhatsApp(t.whatsapp.generic, settings, lang)} className="btn-red" data-testid="guide-wa-btn">
            <MessageCircle size={16}/> {t.contact.cta}
          </button>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            <Link to={pathFor(ROUTE_KEYS.CATALOG, lang)} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB] inline-flex items-center gap-1">{t.nav.catalog} <ArrowRight size={12}/></Link>
            <Link to={pathFor(ROUTE_KEYS.ROUTES, lang)} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB] inline-flex items-center gap-1">{t.nav.routes} <ArrowRight size={12}/></Link>
            <Link to={pathFor(ROUTE_KEYS.RENTAL_FAQ, lang)} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB] inline-flex items-center gap-1">FAQ <ArrowRight size={12}/></Link>
          </div>
        </div>

        {faqs.length > 0 && (
          <div className="mt-12">
            <div className="text-overline text-[#C9A55A] mb-4">FAQ</div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="border border-[#2A2A2A] p-4 group" data-testid={`guide-faq-${i}`}>
                  <summary className="cursor-pointer text-[#C9A55A] text-sm tracking-wide font-semibold list-none flex items-center justify-between">
                    <span>{pick(f.q, lang)}</span>
                    <ChevronRight size={14} className="transition-transform group-open:rotate-90"/>
                  </summary>
                  <p className="mt-3 text-[#F5F1EB]/80 text-sm font-light leading-relaxed">{pick(f.a, lang)}</p>
                </details>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
